import { MineClientRole, isMineClientRole } from './UserRole';

/**
 * MineMembership — one mine a signed-in client user belongs to, and the role they hold there.
 *
 * sprint-217 (epic-036 Phase 1 increment 1D). Frontend mirror of the `mine_membership` JWT
 * claim minted in sprint-215 (`MineMinerClaimTypes.MineMembership`), whose value is
 * `{mineId}:{role}` — e.g. `1234567:MineClientAdmin`. A user with roles at several mines
 * carries several claims.
 *
 * 🔴 THESE ARE FOR RENDERING, NOT FOR AUTHORISING.
 *
 * The claim exists so the client area can draw its navigation without a round trip. Every
 * actual access decision re-reads the database server-side through `IMineMembershipReader`,
 * because a token outlives a revocation: strip someone's membership and their existing JWT
 * still carries the claim until it expires. Anything in this SPA that *grants* access by
 * reading a membership is a bug (DR-005 clause 2 / sprint-215 D3) — and it is a bug that
 * looks perfectly correct in any test using a freshly minted token.
 */
export interface MineMembership {
  /** MSHA mine id, e.g. `1234567`. */
  readonly mineId: string;
  /** The client role held at that mine. */
  readonly role: MineClientRole;
}

/** Separator between mine id and role inside a claim value. Mirrors the backend constant. */
const MINE_MEMBERSHIP_SEPARATOR = ':';

/**
 * Parse one `{mineId}:{role}` claim value.
 *
 * Returns null for anything malformed or for a role this SPA does not recognise as a client
 * role — a claim naming a staff role is not a membership and must not be treated as one.
 */
export function parseMineMembership(value: string | null | undefined): MineMembership | null {
  if (!value) {return null;}

  const separatorAt = value.indexOf(MINE_MEMBERSHIP_SEPARATOR);
  if (separatorAt <= 0) {return null;}

  const mineId = value.slice(0, separatorAt).trim();
  const role = value.slice(separatorAt + 1).trim();

  if (mineId.length === 0 || !isMineClientRole(role)) {return null;}

  return { mineId, role };
}

/**
 * The distinct mine ids in a membership set, in first-seen order.
 *
 * A user can hold more than one role at the same mine, so this is not simply the length of
 * the membership list.
 */
export function distinctMineIds(memberships: readonly MineMembership[]): string[] {
  return [...new Set(memberships.map((m) => m.mineId))];
}
